import { of } from 'rxjs/observable/of';
import { Observable } from 'rxjs';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { switchMap, catchError, exhaustMap, mergeMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { Action } from 'redux-actions';

import {
	getPunchIn,
	postPunchInRecord,
	postPunchInRecordQueue,
	getDayShifts,
	getAllShifts,
	getSuitableShifts,
	getScheduleShifts,
	getDailyShifts,
	postRestPunchRecord,
	deleteRestPunchRecord,
	getRestPunchRecord,
	getPunchInRule,
	getPunchInShift,
	getPunchInShiftsByRange,
} from '../../services/punchIn';

import {
	GET_PUNCH_IN_CALL,
	POST_PUNCH_IN_RECORD_CALL,
	POST_PUNCH_IN_RECORD_QUEUE_CALL,
	GET_DAY_SHIFTS_CALL,
	GET_ALL_SHIFTS_CALL,
	GET_SUITABLE_SHIFTS_CALL,
	GET_SCHEDULE_SHIFTS_CALL,
	GET_DAILY_SHIFTS_CALL,
	POST_REST_PUNCH_RECORD_CALL,
	DELETE_REST_PUNCH_RECORD_CALL,
	GET_REST_PUNCH_RECORD_CALL,
	GET_PUNCH_IN_RULE_CALL,
	GET_PUNCH_IN_SHIFT_CALL,
	GET_PUNCH_IN_SHIFTS_BY_RANGE_CALL,
} from '../types/punchIn';

import {
	getPunchInDone,
	getPunchInFail,
	postPunchInRecordDone,
	postPunchInRecordFail,
	postPunchInRecordQueueDone,
	postPunchInRecordQueueFail,
	getDayShiftsDone,
	getDayShiftsFail,
	getAllShiftsDone,
	getAllShiftsFail,
	getSuitableShiftsDone,
	getSuitableShiftsFail,
	getScheduleShiftsDone,
	getScheduleShiftsFail,
	getDailyShiftsDone,
	getDailyShiftsFail,
	postRestPunchRecordDone,
	postRestPunchRecordFail,
	deleteRestPunchRecordDone,
	deleteRestPunchRecordFail,
	getRestPunchRecordDone,
	getRestPunchRecordFail,
	getPunchInRuleDone,
	getPunchInRuleFail,
	getPunchInShiftDone,
	getPunchInShiftFail,
	getPunchInShiftsByRangeDone,
	getPunchInShiftsByRangeFail,
} from '../actions/punchIn';

export const getPunchInEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_PUNCH_IN_CALL),
	exhaustMap(
		({ payload }) => fromPromise(payload?.configs ? getPunchIn(payload.configs) : getPunchIn()).pipe(
			switchMap(res => {
				if (payload?.onSuccess) payload.onSuccess(res);
				return of(getPunchInDone({ res }));
			}),
			catchError(error => of(getPunchInFail({ error: error?.response?.data }))),
		),
	),
);

export const postPunchInRecordEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_PUNCH_IN_RECORD_CALL),
	mergeMap(
		({ payload }) => {
			const { configs, ...body } = payload;
			return fromPromise(postPunchInRecord(body, configs)).pipe(
				switchMap(res => {
					if (payload.onSuccess) payload.onSuccess(res);
					return of(
						postPunchInRecordDone({ res }),
					);
				}),
				catchError(error => {
					if (payload.onError) payload.onError(error);
					return of(
						postPunchInRecordFail({ error }),
					);
				}),
			);
		},
	),
);

export const postPunchInRecordQueueEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_PUNCH_IN_RECORD_QUEUE_CALL),
	mergeMap(
		({ payload }) => {
			const { configs, ...body } = payload;
			return fromPromise(postPunchInRecordQueue(body, configs)).pipe(
				switchMap(res => {
					if (payload.onSuccess) payload.onSuccess(res);
					return of(
						postPunchInRecordQueueDone({ res }),
					);
				}),
				catchError(error => {
					if (payload.onError) payload.onError(error);
					return of(
						postPunchInRecordQueueFail({ error }),
					);
				}),
			);
		},
	),
);

export const getDayShiftsEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_DAY_SHIFTS_CALL),
	switchMap(
		({ payload }) => fromPromise(getDayShifts(payload)).pipe(
			switchMap(res => of(getDayShiftsDone({ res }))),
			catchError(error => of(getDayShiftsFail({ error: error?.response?.data }))),
		),
	),
);

export const getAllShiftsEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_ALL_SHIFTS_CALL),
	switchMap(
		({ payload }) => fromPromise(getAllShifts(payload)).pipe(
			switchMap(res => of(getAllShiftsDone({ res }))),
			catchError(error => of(getAllShiftsFail({ error: error?.response?.data }))),
		),
	),
);

export const getSuitableShiftsEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_SUITABLE_SHIFTS_CALL),
	switchMap(
		() => fromPromise(getSuitableShifts()).pipe(
			switchMap(res => of(getSuitableShiftsDone({ res }))),
			catchError(error => of(getSuitableShiftsFail({ error: error?.response?.data }))),
		),
	),
);

export const getScheduleShiftsEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_SCHEDULE_SHIFTS_CALL),
	switchMap(
		() => fromPromise(getScheduleShifts()).pipe(
			switchMap(res => of(getScheduleShiftsDone({ res }))),
			catchError(error => of(getScheduleShiftsFail({ error: error?.response?.data }))),
		),
	),
);

export const getDailyShiftsEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_DAILY_SHIFTS_CALL),
	switchMap(
		({ payload }) => fromPromise(getDailyShifts(payload)).pipe(
			switchMap(res => of(getDailyShiftsDone({ res }))),
			catchError(error => of(getDailyShiftsFail({ error: error?.response?.data }))),
		),
	),
);

export const getRestPunchRecordEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_REST_PUNCH_RECORD_CALL),
	switchMap(
		({ payload }) => fromPromise(getRestPunchRecord(payload)).pipe(
			switchMap(res => of(getRestPunchRecordDone({ res }))),
			catchError(error => of(getRestPunchRecordFail({ error: error?.response?.data }))),
		),
	),
);

export const postRestPunchRecordEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_REST_PUNCH_RECORD_CALL),
	switchMap(
		({ payload }) => fromPromise(postRestPunchRecord(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(
					postRestPunchRecordDone({ res }),
				);
			}),
			catchError(error => {
				if (payload.onError) payload.onError(error.response);
				return of(
					postRestPunchRecordFail({ error }),
				);
			}),
		),
	),
);

export const deleteRestPunchRecordEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(DELETE_REST_PUNCH_RECORD_CALL),
	switchMap(
		({ payload }) => fromPromise(deleteRestPunchRecord(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(deleteRestPunchRecordDone({ res }));
			}),
			catchError(error => of(deleteRestPunchRecordFail({ error }))),
		),
	),
);

export const getPunchInRuleEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_PUNCH_IN_RULE_CALL),
	switchMap(
		({ payload }) => fromPromise(getPunchInRule(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(getPunchInRuleDone(res));
			}),
			catchError(error => of(getPunchInRuleFail({ error }))),
		),
	),
);

export const getPunchInShiftEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_PUNCH_IN_SHIFT_CALL),
	switchMap(
		({ payload }) => fromPromise(getPunchInShift(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(getPunchInShiftDone(res));
			}),
			catchError(error => of(getPunchInShiftFail({ error }))),
		),
	),
);

export const getPunchInShiftsByRangeEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_PUNCH_IN_SHIFTS_BY_RANGE_CALL),
	switchMap(
		({ payload }) => fromPromise(getPunchInShiftsByRange(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(getPunchInShiftsByRangeDone(res));
			}),
			catchError(error => of(getPunchInShiftsByRangeFail({ error }))),
		),
	),
);
